import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import { API_ENDPOINTS } from '../../config/api';

// Async Thunks
export const fetchPendingUnits = createAsyncThunk(
    'orders/fetchPendingUnits',
    async ({ adminMobile, page = 1, pageSize = 10, paymentStatus, paymentType, transferMode, search, farmId, fromDate, endDate, withCpf, mouGiven, onboardedByMobile }: {
        adminMobile: string;
        page?: number;
        pageSize?: number;
        paymentStatus?: string;
        paymentType?: string;
        transferMode?: string;
        search?: string;
        farmId?: string;
        fromDate?: string;
        endDate?: string;
        withCpf?: string;
        // MOU filter on the Approved / Paid tab: 'true' = every unit given, 'false' = the rest.
        mouGiven?: string;
        // Scopes results to orders placed by this marketing exec — the "Lead By" filter.
        onboardedByMobile?: string;
    }, { getState, rejectWithValue }) => {
        try {
            const params: any = { page, page_size: pageSize };
            // Treat every "all" sentinel as "no filter". Different call sites use different sentinels
            // ('All Status'/'All Payments'/'All Modes' from the UI dropdowns, plain 'All' from the
            // reset-on-mount defaults). If a sentinel leaks through it becomes a real query filter
            // (e.g. paymentType='All') that matches nothing and zeroes the whole response.
            if (paymentStatus && paymentStatus !== 'All Status' && paymentStatus !== 'All') params.paymentStatus = paymentStatus;
            if (paymentType && paymentType !== 'All Payments' && paymentType !== 'All') params.paymentType = paymentType;
            if (transferMode && transferMode !== 'All Modes' && transferMode !== 'All') params.transferMode = transferMode;
            if (search) params.search = search;

            // Logic: Only send farmId if definitely provided AND not restricted.
            // However, it's safer to check the role from the state if we want to be sure.
            const state = getState() as any;
            const authRole = state.auth?.adminRole || '';
            const adminProfileRole = state.users?.adminProfile?.role || '';
            const effectiveRole = adminProfileRole || authRole;
            const userRoles = effectiveRole ? effectiveRole.split(',').map((r: string) => r.trim()) : [];
            const isSuperAdmin = userRoles.includes('SuperAdmin');

            if (isSuperAdmin && farmId && farmId !== 'All Farms' && farmId !== 'All') {
                params.farmId = farmId;
            }

            if (fromDate) params.from_date = fromDate;
            if (endDate) params.end_date = endDate;
            if (withCpf && withCpf !== 'All') params.withCpf = withCpf === 'true';
            if (mouGiven && mouGiven !== 'All') params.mouGiven = mouGiven === 'true';
            if (onboardedByMobile && onboardedByMobile !== 'All') params.onboardedByMobile = onboardedByMobile;

            const response = await api.get(API_ENDPOINTS.getPendingUnits(), {
                headers: { 'X-Admin-Mobile': adminMobile },
                params
            });
            return response.data; // Return full response to get count
        } catch (error: any) {
            const rawDetail = error?.response?.data?.detail;
            let msg: string;
            if (typeof rawDetail === 'string') {
                msg = rawDetail;
            } else if (Array.isArray(rawDetail)) {
                const first = rawDetail[0];
                if (first && typeof first === 'object' && 'msg' in first) {
                    msg = String(first.msg);
                } else {
                    msg = 'Failed to load orders';
                }
            } else if (rawDetail && typeof rawDetail === 'object' && 'msg' in rawDetail) {
                msg = String(rawDetail.msg);
            } else {
                msg = 'Failed to load orders';
            }
            return rejectWithValue(msg);
        }
    }
);

export const approveOrder = createAsyncThunk(
    'orders/approveOrder',
    async ({
        unitId,
        adminMobile,
        comments,
        unitsChecked,
        transactions
    }: {
        unitId: string;
        adminMobile: string;
        comments?: string;
        unitsChecked?: boolean;
        // Every transaction — including COINS_REDEEM/MONEY_WALLET ones — is sent as its
        // own entry here, verified with the check field matching its payment method.
        transactions?: {
            transactionId: string;
            paymentReceived?: boolean;
            paymentProof?: boolean;
            couponVerified?: boolean;
            surplusReceived?: boolean;
            coinsChecked?: boolean;
            walletChecked?: boolean;
            receivedTransactionId?: string;
        }[];
    }, { dispatch, rejectWithValue, getState }) => {
        try {
            const res = await api.post(API_ENDPOINTS.approveUnit(), {
                orderId: unitId,
                comments,
                unitsChecked,
                transactions
            }, {
                headers: { 'X-Admin-Mobile': adminMobile }
            });
            // Backend returns errors as { statuscode >= 400, message } inside an HTTP 200 body
            const data: any = res?.data;
            if (data && typeof data.statuscode === 'number' && data.statuscode >= 400) {
                return rejectWithValue(typeof data.message === 'string' ? data.message : 'Failed to approve order');
            }
            // Refresh list after success, preserving filters
            const state = getState() as any;
            const { filters } = state.orders;
            dispatch(fetchPendingUnits({
                adminMobile,
                page: filters.page,
                pageSize: filters.pageSize,
                paymentStatus: filters.statusFilter,
                paymentType: filters.paymentTypeFilter,
                transferMode: filters.transferModeFilter,
                farmId: filters.farmFilter,
                search: filters.searchQuery,
                fromDate: filters.fromDateFilter,
                endDate: filters.endDateFilter,
                withCpf: filters.cpfFilter,
                mouGiven: filters.statusFilter === 'PAID' ? filters.mouFilter : 'All',
                onboardedByMobile: filters.leadByFilter
            }));
            return unitId;
        } catch (error: any) {
            const msg = error?.response?.data?.message || error?.message;
            return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to approve order');
        }
    }
);

export const rejectOrder = createAsyncThunk(
    'orders/rejectOrder',
    async ({
        unitId,
        adminMobile,
        comments,
        unitsChecked,
        transactions
    }: {
        unitId: string;
        adminMobile: string;
        comments?: string;
        unitsChecked?: boolean;
        transactions?: {
            transactionId: string;
            paymentReceived?: boolean;
            paymentProof?: boolean;
            couponVerified?: boolean;
            surplusReceived?: boolean;
            coinsChecked?: boolean;
            walletChecked?: boolean;
        }[];
    }, { dispatch, rejectWithValue, getState }) => {
        try {
            await api.post(API_ENDPOINTS.rejectUnit(), {
                orderId: unitId,
                comments,
                unitsChecked,
                transactions
            }, {
                headers: { 'X-Admin-Mobile': adminMobile }
            });
            // Refresh list after success, preserving filters
            const state = getState() as any;
            const { filters } = state.orders;
            dispatch(fetchPendingUnits({
                adminMobile,
                page: filters.page,
                pageSize: filters.pageSize,
                paymentStatus: filters.statusFilter,
                paymentType: filters.paymentTypeFilter,
                transferMode: filters.transferModeFilter,
                farmId: filters.farmFilter,
                search: filters.searchQuery,
                fromDate: filters.fromDateFilter,
                endDate: filters.endDateFilter,
                withCpf: filters.cpfFilter,
                mouGiven: filters.statusFilter === 'PAID' ? filters.mouFilter : 'All',
                onboardedByMobile: filters.leadByFilter
            }));
            return unitId;
        } catch (error: any) {
            return rejectWithValue('Failed to reject order');
        }
    }
);

export const sendOrderForApproval = createAsyncThunk(
    'orders/sendOrderForApproval',
    async ({
        orderId,
        adminMobile,
        comment
    }: {
        orderId: string;
        adminMobile: string;
        comment?: string;
    }, { dispatch, rejectWithValue, getState }) => {
        try {
            await api.post(API_ENDPOINTS.sendOrderForApproval(orderId), { comment: comment || undefined }, {
                headers: { 'X-Admin-Mobile': adminMobile }
            });
            // Refresh list after success, preserving filters
            const state = getState() as any;
            const { filters } = state.orders;
            dispatch(fetchPendingUnits({
                adminMobile,
                page: filters.page,
                pageSize: filters.pageSize,
                paymentStatus: filters.statusFilter,
                paymentType: filters.paymentTypeFilter,
                transferMode: filters.transferModeFilter,
                farmId: filters.farmFilter,
                search: filters.searchQuery,
                fromDate: filters.fromDateFilter,
                endDate: filters.endDateFilter,
                withCpf: filters.cpfFilter,
                mouGiven: filters.statusFilter === 'PAID' ? filters.mouFilter : 'All',
                onboardedByMobile: filters.leadByFilter
            }));
            return orderId;
        } catch (error: any) {
            const rawDetail = error?.response?.data?.detail || error?.response?.data?.message;
            const msg = typeof rawDetail === 'string' ? rawDetail : 'Failed to send order for approval';
            return rejectWithValue(msg);
        }
    }
);

export const editTransaction = createAsyncThunk(
    'orders/editTransaction',
    async (payload: {
        orderId: string;
        transactionId: string;
        paymentMethod: string;
        reason?: string;
        amount?: number;
        transactionDate?: string;
        transferMode?: string;
        utrNumber?: string;
        paymentScreenshotUrl?: string;
        accountHolderName?: string;
        bankName?: string;
        voucherImageUrl?: string;
        cashierName?: string;
        cashierPhone?: string;
        upiId?: string;
        chequeFrontImage?: string;
        chequeBackImage?: string;
        chequeDate?: string;
        adminMobile: string;
        asEmployee?: boolean;
    }, { rejectWithValue }) => {
        try {
            const { adminMobile, asEmployee, ...body } = payload;
            const endpoint = asEmployee
                ? API_ENDPOINTS.editOrderTransaction(body.orderId)
                : API_ENDPOINTS.editTransaction();
            const response = await api.post(endpoint, { ...body, source: 'animalkart_dashboard' }, {
                headers: { 'X-Admin-Mobile': adminMobile }
            });
            return response.data;
        } catch (error: any) {
            const rawDetail = error?.response?.data?.detail || error?.response?.data?.message;
            const msg = typeof rawDetail === 'string' ? rawDetail : 'Failed to edit transaction';
            return rejectWithValue(msg);
        }
    }
);

export interface OrdersState {
    pendingUnits: any[];
    loading: boolean;
    error: string | null;
    // requestId of the most recently dispatched fetchPendingUnits — lets fulfilled/rejected
    // ignore a stale response that resolves after a newer request was already sent (e.g. the
    // admin clears a search right after typing one; the two requests can resolve out of
    // order, and without this guard the slower/older one would overwrite the fresher result).
    latestRequestId: string | null;
    actionLoading: boolean;
    editTransactionLoading: boolean;
    totalCount: number;
    totalAllOrders: number;
    pendingSubmissionCount: number;
    pendingAdminApprovalCount: number;
    pendingSuperAdminApprovalCount: number;
    pendingSuperAdminRejectionCount: number;
    paidCount: number;
    rejectedCount: number;
    paymentDueCount: number;
    cancelledCount: number;
    totalUnitsApprovedCount: number;
    totalAmountCollected: number;
    totalCoinsRedeemed: number;
    totalCouponsRedeemed: number;
    totalCashCollected: number;
    totalOnlineAmountCollected: number;
    statusCounts: { [key: string]: number };
    // The active calculator model, as /purchases/admin/units/pending reports it
    // (utils/calculator_settings.active_model_figures). Fields are null when unknown.
    activeModel: { model_version: number | null; first_year_firstbcpf: number | null; first_year_secondbcpf: number | null } | null;
    filters: {
        searchQuery: string;
        paymentTypeFilter: string;
        transferModeFilter: string;
        statusFilter: string;
        farmFilter: string;
        fromDateFilter: string;
        endDateFilter: string;
        cpfFilter: string;
        mouFilter: string;
        // Mobile number of the marketing exec to filter "Lead By" on — 'All' means no filter.
        leadByFilter: string;
        page: number;
        pageSize: number;
    };
    expansion: {
        expandedOrderId: string | null;
        activeUnitIndex: number | null;
    };
}

const getInitialExpansion = () => {
    try {
        return {
            expandedOrderId: localStorage.getItem('orders_expandedOrderId') || null,
            activeUnitIndex: localStorage.getItem('orders_activeUnitIndex') ? Number(localStorage.getItem('orders_activeUnitIndex')) : null,
        };
    } catch {
        return { expandedOrderId: null, activeUnitIndex: null };
    }
};

const initialState: OrdersState = {
    pendingUnits: [],
    loading: false,
    error: null,
    latestRequestId: null,
    actionLoading: false,
    editTransactionLoading: false,
    totalCount: 0,
    totalAllOrders: 0,
    pendingSubmissionCount: 0,
    pendingAdminApprovalCount: 0,
    pendingSuperAdminApprovalCount: 0,
    pendingSuperAdminRejectionCount: 0,
    paidCount: 0,
    rejectedCount: 0,
    paymentDueCount: 0,
    cancelledCount: 0,
    totalUnitsApprovedCount: 0,
    totalAmountCollected: 0,
    totalCoinsRedeemed: 0,
    totalCouponsRedeemed: 0,
    totalCashCollected: 0,
    totalOnlineAmountCollected: 0,
    statusCounts: {},
    activeModel: null,
    filters: {
        searchQuery: localStorage.getItem('orders_searchQuery') || '',
        paymentTypeFilter: localStorage.getItem('orders_paymentTypeFilter') || 'All Payments',
        transferModeFilter: localStorage.getItem('orders_transferModeFilter') || 'All Modes',
        // NOTE: statusFilter is intentionally NOT restored from localStorage. OrdersTab resets it
        // to 'All Status' on every mount, so restoring a stale value here only feeds a wrong
        // paymentStatus into the first-render fetch, which then races with (and can overwrite) the
        // correct 'All Status' fetch. Initializing to the reset value keeps the first fetch correct.
        statusFilter: 'All Status',
        farmFilter: localStorage.getItem('orders_farmFilter') || 'All Farms',
        fromDateFilter: localStorage.getItem('orders_fromDateFilter') || '',
        endDateFilter: localStorage.getItem('orders_endDateFilter') || '',
        cpfFilter: localStorage.getItem('orders_cpfFilter') || 'All',
        mouFilter: localStorage.getItem('orders_mouFilter') || 'All',
        leadByFilter: localStorage.getItem('orders_leadByFilter') || 'All',
        page: 1,
        pageSize: 15,
    },
    expansion: getInitialExpansion(),
};

const ordersSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        setSearchQuery(state, action: PayloadAction<string>) {
            state.filters.searchQuery = action.payload;
            state.filters.page = 1; // Reset to page 1 on search
        },
        // Optimize: Batch update filters
        setAllFilters(state, action: PayloadAction<Partial<typeof state.filters>>) {
            if (action.payload.page !== undefined) state.filters.page = action.payload.page;
            if (action.payload.statusFilter !== undefined) state.filters.statusFilter = action.payload.statusFilter;
            if (action.payload.paymentTypeFilter !== undefined) state.filters.paymentTypeFilter = action.payload.paymentTypeFilter;
            if (action.payload.transferModeFilter !== undefined) state.filters.transferModeFilter = action.payload.transferModeFilter;
            if (action.payload.farmFilter !== undefined) state.filters.farmFilter = action.payload.farmFilter;
            if (action.payload.fromDateFilter !== undefined) state.filters.fromDateFilter = action.payload.fromDateFilter;
            if (action.payload.endDateFilter !== undefined) state.filters.endDateFilter = action.payload.endDateFilter;
            if (action.payload.cpfFilter !== undefined) state.filters.cpfFilter = action.payload.cpfFilter;
            if (action.payload.mouFilter !== undefined) state.filters.mouFilter = action.payload.mouFilter;
            if (action.payload.leadByFilter !== undefined) state.filters.leadByFilter = action.payload.leadByFilter;
        },
        setPaymentFilter: (state, action: PayloadAction<string>) => {
            state.filters.paymentTypeFilter = action.payload;
        },
        setTransferModeFilter: (state, action: PayloadAction<string>) => {
            state.filters.transferModeFilter = action.payload;
        },
        setStatusFilter: (state, action: PayloadAction<string>) => {
            state.filters.statusFilter = action.payload;
        },
        setFarmFilter: (state, action: PayloadAction<string>) => {
            state.filters.farmFilter = action.payload;
        },
        setFromDateFilter: (state, action: PayloadAction<string>) => {
            state.filters.fromDateFilter = action.payload;
        },
        setEndDateFilter: (state, action: PayloadAction<string>) => {
            state.filters.endDateFilter = action.payload;
        },
        setCpfFilter: (state, action: PayloadAction<string>) => {
            state.filters.cpfFilter = action.payload;
        },
        setMouFilter: (state, action: PayloadAction<string>) => {
            state.filters.mouFilter = action.payload;
        },
        setLeadByFilter: (state, action: PayloadAction<string>) => {
            state.filters.leadByFilter = action.payload;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.filters.page = action.payload;
        },
        setExpandedOrderId: (state, action: PayloadAction<string | null>) => {
            state.expansion.expandedOrderId = action.payload;
        },
        setActiveUnitIndex: (state, action: PayloadAction<number | null>) => {
            state.expansion.activeUnitIndex = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Fetch Pending Units
        builder.addCase(fetchPendingUnits.pending, (state, action) => {
            state.loading = true;
            state.error = null;
            state.latestRequestId = action.meta.requestId;
        });
        builder.addCase(fetchPendingUnits.fulfilled, (state, action) => {
            // A newer request has since been dispatched — this response is for a search/filter
            // combination that's no longer current, so applying it would show stale counts/rows.
            if (action.meta.requestId !== state.latestRequestId) return;
            state.loading = false;
            // Handle response format: { orders: [], total_count: 123 } or just array
            const payload = action.payload;
            let currentCount = 0;

            if (payload && Array.isArray(payload.orders)) {
                state.pendingUnits = payload.orders;
                // Prefer total_filtered for pagination if available, otherwise fallback
                currentCount = payload.total_filtered ?? (payload.total_count || payload.total || payload.count || payload.orders.length);
                state.totalCount = currentCount;

                // Sync all counts from the response
                if (typeof payload.total_all_orders === 'number') {
                    state.totalAllOrders = payload.total_all_orders;
                    state.statusCounts['All Status'] = payload.total_all_orders;
                }

                if (payload.pending_submission_count !== undefined) {
                    const c = Number(payload.pending_submission_count);
                    state.pendingSubmissionCount = c;
                    state.statusCounts['PENDING_SUBMISSION'] = c;
                }
                if (payload.pending_admin_approval_count !== undefined) {
                    const c = Number(payload.pending_admin_approval_count);
                    state.pendingAdminApprovalCount = c;
                    state.statusCounts['PENDING_ADMIN_VERIFICATION'] = c;
                }
                if (payload.pending_super_admin_approval_count !== undefined) {
                    const c = Number(payload.pending_super_admin_approval_count);
                    state.pendingSuperAdminApprovalCount = c;
                    state.statusCounts['PENDING_SUPER_ADMIN_VERIFICATION'] = c;
                }
                if (payload.pending_super_admin_rejection_count !== undefined) {
                    const c = Number(payload.pending_super_admin_rejection_count);
                    state.pendingSuperAdminRejectionCount = c;
                    state.statusCounts['PENDING_SUPER_ADMIN_REJECTION'] = c;
                }
                if (payload.paid_count !== undefined) {
                    const c = Number(payload.paid_count);
                    state.paidCount = c;
                    state.statusCounts['PAID'] = c;
                }
                if (payload.rejected_count !== undefined) {
                    const c = Number(payload.rejected_count);
                    state.rejectedCount = c;
                    state.statusCounts['REJECTED'] = c;
                }
                if (payload.payment_due_count !== undefined) {
                    const c = Number(payload.payment_due_count);
                    state.paymentDueCount = c;
                    state.statusCounts['PENDING_PAYMENT'] = c;
                }
                if (payload.cancelled_count !== undefined) {
                    const c = Number(payload.cancelled_count);
                    state.cancelledCount = c;
                    state.statusCounts['CANCELLED'] = c;
                }
                if (payload.total_units_approved_count !== undefined) {
                    state.totalUnitsApprovedCount = Number(payload.total_units_approved_count);
                }
                if (payload.total_amount_collected !== undefined) {
                    state.totalAmountCollected = Number(payload.total_amount_collected);
                }
                if (payload.total_coins_redeemed !== undefined) {
                    state.totalCoinsRedeemed = Number(payload.total_coins_redeemed);
                }
                if (payload.total_coupons_redeemed !== undefined) {
                    state.totalCouponsRedeemed = Number(payload.total_coupons_redeemed);
                }
                if (payload.total_cash_collected !== undefined) {
                    state.totalCashCollected = Number(payload.total_cash_collected);
                }
                if (payload.total_online_amount_collected !== undefined) {
                    state.totalOnlineAmountCollected = Number(payload.total_online_amount_collected);
                }
                if (payload.model_version !== undefined) {
                    state.activeModel = {
                        model_version: payload.model_version ?? null,
                        first_year_firstbcpf: payload.first_year_firstbcpf ?? null,
                        first_year_secondbcpf: payload.first_year_secondbcpf ?? null,
                    };
                }
            }
        });
        builder.addCase(fetchPendingUnits.rejected, (state, action) => {
            if (action.meta.requestId !== state.latestRequestId) return;
            state.loading = false;
            state.error = action.payload as string;
        });

        // Approve Order
        builder.addCase(approveOrder.pending, (state) => {
            state.actionLoading = true;
        });
        builder.addCase(approveOrder.fulfilled, (state) => {
            state.actionLoading = false;
        });
        builder.addCase(approveOrder.rejected, (state) => {
            state.actionLoading = false;
        });

        // Reject Order
        builder.addCase(rejectOrder.pending, (state) => {
            state.actionLoading = true;
        });
        builder.addCase(rejectOrder.fulfilled, (state) => {
            state.actionLoading = false;
        });
        builder.addCase(rejectOrder.rejected, (state) => {
            state.actionLoading = false;
        });

        // Send Order For Approval (employee)
        builder.addCase(sendOrderForApproval.pending, (state) => {
            state.actionLoading = true;
        });
        builder.addCase(sendOrderForApproval.fulfilled, (state) => {
            state.actionLoading = false;
        });
        builder.addCase(sendOrderForApproval.rejected, (state) => {
            state.actionLoading = false;
        });

        // Edit Transaction
        builder.addCase(editTransaction.pending, (state) => {
            state.editTransactionLoading = true;
        });
        builder.addCase(editTransaction.fulfilled, (state) => {
            state.editTransactionLoading = false;
        });
        builder.addCase(editTransaction.rejected, (state) => {
            state.editTransactionLoading = false;
        });
    }
});

export const {
    setSearchQuery,
    setAllFilters,
    setPaymentFilter,
    setTransferModeFilter,
    setStatusFilter,
    setFarmFilter,
    setFromDateFilter,
    setEndDateFilter,
    setCpfFilter,
    setMouFilter,
    setLeadByFilter,
    setPage,
    setExpandedOrderId,
    setActiveUnitIndex,
} = ordersSlice.actions;


export const ordersReducer = ordersSlice.reducer;
export default ordersSlice.reducer;
